import { parseAiSpatialFilter } from 'components/DisasterGPT/utils/aiMapSpatialFilter';
import {
  CUSTOM_AREA_LABEL,
  isCustomSpatialScope,
  type SitrepSpatialScope,
} from 'components/Reports/utils/resolveSitrepSpatialFilter';

export type ReportAreaScopeSource = 'incident' | 'aor' | 'asset' | 'custom';

export type PersistedAreaScopeEntry = {
  spatialFilter?: unknown;
  spatialFilterLabel?: string;
  scopeSource?: ReportAreaScopeSource;
};

export type EffectiveReportAreaScope = {
  spatialFilter: SitrepSpatialScope['spatialFilter'];
  spatialFilterLabel: string;
  source: ReportAreaScopeSource;
};

export function customScopeFromDataSource(
  entry: PersistedAreaScopeEntry | null | undefined
): Pick<SitrepSpatialScope, 'spatialFilter' | 'spatialFilterLabel'> | null {
  if (!isCustomSpatialScope(entry?.spatialFilterLabel)) return null;
  const spatialFilter = parseAiSpatialFilter(entry?.spatialFilter);
  if (!spatialFilter) return null;
  return { spatialFilter, spatialFilterLabel: CUSTOM_AREA_LABEL };
}

export function scopeSourceFromScope(
  scope: Pick<EffectiveReportAreaScope, 'spatialFilterLabel' | 'source'> | null
): ReportAreaScopeSource | null {
  if (!scope) return null;
  if (isCustomSpatialScope(scope.spatialFilterLabel)) return 'custom';
  return scope.source;
}

export function firstSectionAreaScope(
  sections:
    | Array<{
        data_sources?: Array<PersistedAreaScopeEntry & { source?: string }>;
      }>
    | null
    | undefined
): PersistedAreaScopeEntry | null {
  if (!Array.isArray(sections)) return null;
  for (const section of sections) {
    const sources = Array.isArray(section.data_sources)
      ? section.data_sources
      : [];
    const entry = sources.find((ds) => ds?.spatialFilter);
    if (entry) return entry;
  }
  return null;
}

export function buildEntityListDataSource(params: {
  source: string;
  scope: EffectiveReportAreaScope | null;
}): {
  source: string;
  spatialFilter?: EffectiveReportAreaScope['spatialFilter'];
  spatialFilterLabel?: string;
  scopeSource?: ReportAreaScopeSource;
} {
  const scopeSource = scopeSourceFromScope(params.scope);
  return {
    source: params.source,
    ...(params.scope
      ? {
          spatialFilter: params.scope.spatialFilter,
          spatialFilterLabel: params.scope.spatialFilterLabel,
          ...(scopeSource ? { scopeSource } : {}),
        }
      : {}),
  };
}
